import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ShieldAlert, Sparkles } from 'lucide-react';
import type {
  AiLogApiRow,
  AiLogQueryParams,
  AiUsageBucket,
  AiUsageResponse,
  AiUsageWindow,
} from '@command-center/logging-sdk';
import { formatMicroUsd, rowCostMicroUsd } from '@oneup/shared';
import { Badge, Button, Card } from '@command-center/ui-system';
import { LogDrawer, LogField } from './log-drawer';
import {
  LogDegradedBanner,
  LogEmptyState,
  LogErrorState,
  LogSkeleton,
} from './log-states';
import { LogFilterBar, type FilterField } from './LogFilterBar';
import { logQueryKey, rangeToSince, useLogFilters } from './log-filters';
import { composeAiLogParams, describeCost, logViewState, visibleFilters } from './log-view-model';
import type { AiLogViewerProps } from './types';

const STATUSES = ['success', 'sent', 'error', 'discarded', 'pending_confirmation', 'pending_send'];
const PROVIDERS = ['openai', 'anthropic', 'google', 'vapi'];
const COST_STATES = [
  { value: 'priced', label: 'Priced' },
  { value: 'estimated', label: 'Estimated' },
  { value: 'unknown', label: 'Unpriced' },
];

/**
 * The one AI invocation viewer, for every portal.
 *
 * As with Activity, `scope` is enforced server-side so a portal-, User- or
 * Organization-scoped view is the same master log narrowed by the API.
 *
 * This shows operational metadata only: provider, model, token counts, cost and
 * provenance, duration, outcome, and correlation. Prompts and responses are
 * stored as short previews by the writer and are not surfaced here — an
 * operator debugging spend does not need to read what someone asked.
 */
export function AiLogViewer({
  client,
  scope,
  storageKey,
  showUsage = true,
  hiddenFilters = [],
  labels,
  renderRowActions,
}: AiLogViewerProps) {
  const { values, range, setValue, setRange, clear } = useLogFilters(storageKey, '30');
  const [selected, setSelected] = useState<AiLogApiRow | null>(null);

  // Serialized so an inline `scope` literal does not change identity every
  // render and rerun the memos.
  const scopeKey = JSON.stringify(scope ?? {});

  const fields = useMemo<FilterField[]>(
    () =>
      visibleFilters(
        [
          { key: 'status', label: 'Status', options: STATUSES.map(opt) },
          { key: 'provider', label: 'Provider', options: PROVIDERS.map(opt) },
          { key: 'costState', label: 'Cost', options: COST_STATES },
          { key: 'model', label: 'Model', placeholder: 'Model' },
          { key: 'appId', label: 'App', placeholder: 'App id' },
          { key: 'clientId', label: 'Organization', placeholder: 'Org id' },
          { key: 'feature', label: 'Feature', placeholder: 'Feature' },
          { key: 'switchKey', label: 'Switch', placeholder: 'Switch key' },
        ] satisfies FilterField[],
        hiddenFilters,
        JSON.parse(scopeKey) as object,
      ),
    [hiddenFilters, scopeKey],
  );

  const params = useMemo<AiLogQueryParams>(
    () => composeAiLogParams(values, JSON.parse(scopeKey) as never),
    [values, scopeKey],
  );

  const usageEnabled = showUsage && Boolean(client.usage);

  // `range` keys the query; the timestamp it resolves to does not. rangeToSince
  // reads the clock, so a key containing it would differ on every render and
  // refetch in a loop.
  const usage = useQuery({
    queryKey: logQueryKey('ai-usage', params, range),
    queryFn: () => client.usage!({ ...params, since: rangeToSince(range) }),
    enabled: usageEnabled,
    refetchInterval: 60_000,
  });

  const logs = useInfiniteQuery({
    queryKey: logQueryKey('ai-logs', params, range),
    queryFn: ({ pageParam }) =>
      client.list({ ...params, since: rangeToSince(range), cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 60_000,
  });

  const pages = logs.data?.pages ?? [];
  const rows = pages.flatMap((p) => p.items);
  const degraded = pages[0]?.degraded;
  const handleChange = useCallback(
    (key: string, value: string | undefined) => setValue(key, value),
    [setValue],
  );

  const state = logViewState({
    isError: logs.isError,
    isLoading: logs.isLoading,
    itemCount: rows.length,
  });

  return (
    <div className="space-y-3">
      <LogFilterBar
        fields={fields}
        values={values}
        onChange={handleChange}
        onClear={clear}
        range={range}
        onRangeChange={setRange}
        searchPlaceholder={labels?.searchPlaceholder ?? 'Search model, feature, or error…'}
      />

      {usageEnabled && (
        <UsagePanel
          data={usage.data}
          loading={usage.isLoading}
          failed={usage.isError}
        />
      )}

      {degraded && <LogDegradedBanner note="The AI log store could not be reached." />}

      {state === 'error' ? (
        <LogErrorState title="Couldn't load AI logs" error={logs.error} />
      ) : state === 'loading' ? (
        <LogSkeleton className="h-[320px]" />
      ) : (
        <Card className="overflow-hidden">
          {state === 'empty' ? (
            <LogEmptyState
              icon={Sparkles}
              message={labels?.empty ?? 'No AI calls match these filters.'}
            />
          ) : (
            <>
              <ul className="divide-y divide-border">
                {rows.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(row)}
                      className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-muted/40"
                    >
                      <Badge variant={statusVariant(row.status)} className="mt-0.5">
                        {formatLabel(row.status)}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium">
                          {formatLabel(row.action_type)}
                          {row.feature && row.feature !== 'other' && (
                            <span className="text-muted-foreground"> · {row.feature}</span>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
                          {row.model && <span>{row.model}</span>}
                          {(row.client_label || row.client_id) && (
                            <span title={row.client_id ?? undefined}>
                              {row.client_label || row.client_id}
                            </span>
                          )}
                          <span>
                            {((row.tokens_input ?? 0) + (row.tokens_output ?? 0)).toLocaleString()}{' '}
                            tokens
                          </span>
                          <CostLabel row={row} />
                        </div>
                        {row.error_message && (
                          <div className="mt-1 truncate text-[12px] text-destructive">
                            {row.error_message}
                          </div>
                        )}
                      </div>
                      <time className="whitespace-nowrap pt-0.5 text-[11px] text-muted-foreground">
                        {new Date(row.updated_at).toLocaleString()}
                      </time>
                    </button>
                  </li>
                ))}
              </ul>
              {logs.hasNextPage && (
                <div className="border-t border-border p-2 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => logs.fetchNextPage()}
                    disabled={logs.isFetchingNextPage}
                  >
                    {logs.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {selected && (
        <AiLogDetail
          row={selected}
          onClose={() => setSelected(null)}
          renderRowActions={renderRowActions}
        />
      )}
    </div>
  );
}

function UsagePanel({
  data,
  loading,
  failed,
}: {
  data?: AiUsageResponse;
  loading: boolean;
  failed: boolean;
}) {
  if (loading) return <Card className="h-[200px] animate-pulse" />;
  if (failed) return null;
  if (!data) return null;

  if (data.degraded) {
    return (
      <Card className="flex items-start gap-2 border-warning/40 bg-warning/5 p-3 text-[12px]">
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
        <span>
          {data.note ?? 'AI usage could not be aggregated.'} Totals are unavailable — this is not
          zero spend.
        </span>
      </Card>
    );
  }

  const selected = data.selected;
  const unpriced = selected?.unpricedCalls ?? 0;
  const estimated = selected?.estimatedCalls ?? 0;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">AI spend</h2>
        <span className="text-[11px] text-muted-foreground">
          updated {new Date(data.generatedAt).toLocaleTimeString()}
        </span>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SpendTile label="Today" window={data.today} />
        <SpendTile label="Last 7 days" window={data.last7Days} />
        <SpendTile label="Last 30 days" window={data.last30Days} />
        <SpendTile label="Selected range" window={selected} highlight />
      </div>

      {(unpriced > 0 || estimated > 0) && (
        <div className="mt-3 space-y-1 text-[12px] text-muted-foreground">
          {unpriced > 0 && (
            <p>
              <strong className="text-foreground">{unpriced.toLocaleString()}</strong> call
              {unpriced === 1 ? '' : 's'} in this range have no usable cost, so the totals are a
              lower bound. An unpriced call is not a free call.
            </p>
          )}
          {estimated > 0 && (
            <p>
              <strong className="text-foreground">{estimated.toLocaleString()}</strong> call
              {estimated === 1 ? '' : 's'} are priced from a provisional table. Treat those
              figures as estimates, not billing.
            </p>
          )}
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Breakdown title="By model" rows={data.byModel} />
        <Breakdown title="By organization" rows={data.byClient} />
        <Breakdown title="By app" rows={data.byApp} />
        <Breakdown title="By feature" rows={data.byFeature} />
      </div>
      {(data.bySwitch?.length ?? 0) > 0 && (
        <div className="mt-4">
          <Breakdown title="By Switch" rows={data.bySwitch} />
        </div>
      )}
    </Card>
  );
}

function SpendTile({
  label,
  window: usage,
  highlight,
}: {
  label: string;
  window?: AiUsageWindow;
  highlight?: boolean;
}) {
  // Every field is optional-guarded: a rollup that failed must render as "—"
  // rather than throwing on an absent total.
  const calls = usage?.calls ?? 0;
  const tokens = (usage?.tokensInput ?? 0) + (usage?.tokensOutput ?? 0);
  return (
    <div
      className={`rounded-lg border p-3 ${
        highlight ? 'border-primary bg-selection' : 'border-border bg-muted/30'
      }`}
    >
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">
        {usage ? formatMicroUsd(usage.costMicroUsd) : '—'}
      </div>
      <div className="mt-0.5 text-[12px] text-muted-foreground tabular-nums">
        {calls.toLocaleString()} {calls === 1 ? 'call' : 'calls'} · {tokens.toLocaleString()} tokens
      </div>
    </div>
  );
}

/**
 * A usage breakdown.
 *
 * `label` is honoured for EVERY dimension, not just models. It used to be gated
 * behind a `labelKey` flag that only "By model" passed, which is why the
 * organization breakdown rendered raw Airtable record ids — the server could
 * have sent a name and the table would still have shown `rec...`.
 */
function Breakdown({ title, rows }: { title: string; rows?: AiUsageBucket[] }) {
  const list = rows ?? [];
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {list.length === 0 ? (
        <p className="mt-2 text-[12px] text-muted-foreground">No calls in this window.</p>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[300px] text-[12px]">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pb-1 font-medium">Name</th>
                <th className="pb-1 text-right font-medium">Calls</th>
                <th className="pb-1 text-right font-medium">Tokens</th>
                <th className="pb-1 text-right font-medium">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.map((row) => (
                <tr key={row.bucket}>
                  <td className="py-1.5 pr-2">
                    <span
                      className="font-medium text-foreground"
                      // The stable key stays reachable even when a name is
                      // shown, so an operator can still correlate with the log.
                      title={row.label && row.label !== row.bucket ? row.bucket : undefined}
                    >
                      {row.label || friendlyBucket(row.bucket)}
                    </span>
                    {row.unpricedCalls > 0 && (
                      <Badge variant="warning" className="ml-1.5 text-[9px]">
                        {row.unpricedCalls} unpriced
                      </Badge>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{row.calls.toLocaleString()}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {(row.tokensInput + row.tokensOutput).toLocaleString()}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatMicroUsd(row.costMicroUsd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * A cost we do not have is reported as unknown, never as zero.
 *
 * Rendering an unpriced call as $0.00 is how a spend total silently becomes a
 * lower bound that reads like a measurement.
 */
function CostLabel({ row }: { row: AiLogApiRow }) {
  const cost = describeCost(row, formatMicroUsd, rowCostMicroUsd);
  return <span className={cost.known ? undefined : 'text-warning'}>{cost.label}</span>;
}

function AiLogDetail({
  row,
  onClose,
  renderRowActions,
}: {
  row: AiLogApiRow;
  onClose: () => void;
  renderRowActions?: (row: AiLogApiRow) => React.ReactNode;
}) {
  const meta = (row.metadata ?? {}) as Record<string, unknown>;
  return (
    <LogDrawer
      title={`${formatLabel(row.action_type)}${row.model ? ` · ${row.model}` : ''}`}
      description={
        <>
          <Badge variant={statusVariant(row.status)}>{formatLabel(row.status)}</Badge>
          <Badge variant="muted">{new Date(row.updated_at).toLocaleString()}</Badge>
        </>
      }
      icon={Sparkles}
      onClose={onClose}
    >
      <div className="mt-4 space-y-4 overflow-y-auto pb-8 text-[13px]">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Input tokens" value={(row.tokens_input ?? 0).toLocaleString()} />
          <Stat label="Output tokens" value={(row.tokens_output ?? 0).toLocaleString()} />
          <Stat
            label="Cost"
            value={
              row.cost_micro_usd != null || (row.cost_cents ?? 0) > 0
                ? formatMicroUsd(rowCostMicroUsd(row))
                : 'Unknown'
            }
          />
          <Stat label="Duration" value={meta.durationMs ? `${meta.durationMs} ms` : '—'} />
        </div>

        {renderRowActions?.(row)}

        <LogField label="App / Organization">
          <div>{row.app_id ?? row.portal ?? '—'}</div>
          <div>{row.client_label || row.client_id || 'Not attributed'}</div>
          {/*
            The drawer is where someone goes to reconcile a row, so the stored
            id is shown outright rather than hidden behind a tooltip.
          */}
          {row.client_id && row.client_label && row.client_label !== row.client_id && (
            <div className="text-[11px] text-muted-foreground">{row.client_id}</div>
          )}
        </LogField>
        {row.provider && <LogField label="Provider">{row.provider}</LogField>}
        {row.cost_source && <LogField label="Cost source">{row.cost_source}</LogField>}
        {meta.workflow ? <LogField label="Workflow">{String(meta.workflow)}</LogField> : null}
        {(row.switch_key || row.correlation_id) && (
          <LogField label="Run">
            {row.switch_key && <div>Switch: {row.switch_key}</div>}
            {row.correlation_id && <div>Correlation: {row.correlation_id}</div>}
          </LogField>
        )}

        {row.error_message && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Error
            </div>
            <p className="mt-0.5 text-destructive">{row.error_message}</p>
          </div>
        )}

        {Object.keys(meta).length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Metadata
            </div>
            <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-muted/50 p-2 text-[11px] leading-relaxed">
              {JSON.stringify(meta, null, 2)}
            </pre>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">
          Prompts and responses are stored as short previews only, and are not shown here. Full
          conversations, provider payloads, and attachment contents are never written to the AI log.
        </p>
      </div>
    </LogDrawer>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function friendlyBucket(bucket: string): string {
  return bucket === '(unattributed)' ? 'Not attributed' : bucket;
}

function opt(value: string) {
  return { value, label: value.replaceAll('_', ' ') };
}

function formatLabel(value: string): string {
  return value.replaceAll('_', ' ');
}

function statusVariant(status: string): 'success' | 'warning' | 'destructive' | 'muted' {
  if (status === 'success' || status === 'sent') return 'success';
  if (status === 'error') return 'destructive';
  if (status === 'discarded') return 'muted';
  return 'warning';
}
